import React from 'react';
import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';
import moment from 'moment';

import { authUserState } from './common';
import { checkRights, clearEmptyParams, formatDate, getRubs } from '../functions';
import { buttonTitles, VATTitles } from '../dictionary';
import { PayCardIcon, PayCashIcon, PayReceiptIcon } from '../images/icons';

/** Типы накладных */
export const InvoiceTypes = [
	{ Id : 1, Name : "Номер", ShortName : "№" },
	{ Id : 2, Name : "Товарная накладная", ShortName : "ТН" },
	{ Id : 3, Name : "Счет-фактура", ShortName : "СФ" }
];

/** Статусы накладных */
export const InvoiceStatuses = [
	{ Id : 1, Name: "Оприходована" },
	{ Id : 2, Name: "Подтверждена" },
	{ Id : 3, Name: "Оплачена" }
];

/** Способы оплаты накладных */
export const InvoicePaymentTypes = [
	{ Id : 2, Name : "По счету", Icon : <PayReceiptIcon />, Priority : 1 },
	{ Id : 3, Name : "Бизнес-карта", Icon : <PayCardIcon />, Priority : 3 },
	{ Id : 1, Name : "Наличные", Icon : <PayCashIcon />, Priority : 2 }
];

/** Количество календарных дней до срока оплаты */
export function getPaymentDaysLeft(deadline, today = moment().startOf("day")) {
	return Math.ceil(moment(deadline).diff(moment(today).startOf("day"), "hours") / 24);
}

/** Флаг наступившего или прошедшего срока оплаты */
export function isPaymentAlert(deadline, today = moment().startOf("day")) {
	return getPaymentDaysLeft(deadline, today) <= 0;
}

/** Класс работы с разделом накладных */
export class InvoiceState {
	constructor() {
		this.Filter = new Filter();
		this.IsSendRequest = false;

		this.Invoices = [];
		this.HasMore = false;

		this.Providers = [];
		this.ShopListList = [];

		this.Invoice = undefined;
		
		this.Today = moment().hour(0).minute(0).second(0);

	}

	/** Право управления накладными */
	get CanManage () { return checkRights("ManageInvoices"); }
	/** Право подтверждения накладных */
	get CanAccept () { return checkRights("AcceptInvoices"); }
	/** Право оплаты накладных */
	get CanPayment () { return checkRights("PaymentInvoices"); }
	/** Право открытия раздела накладных */
	get CanView () { return this.CanManage || this.CanAccept; }

	/**
	 * Получение доступных пользователю складов.
	 * @returns {[{ FilialId : number, Id : number, Name : string }]} склады доступных филиалов
	 */
	get Stocks () {
		if (_.isUndefined(authUserState) || _.isEmpty(authUserState.Filials)) return [];

		return _.flatten(_.map(authUserState.Filials, (filial) => {
			return _.map(filial.Stocks, (stock) => {
				return { FilialId : filial.Id, Id : stock.Id, Name : stock.Name };
			});
		}));
	}

	/**
	 * Формирование списка закупок для выбора.
	 * @returns {[{ Id : number, Name : string }]} подготовленные списки закупок
	 */
	get ShopLists () {
		return _.map(this.ShopListList, (list) => {
			return { Id : list.Id, Name : list.Provider + " - " + list.Stock + " от " + formatDate(list.Date) };
		});
	}

	/**
	 * Разбор данных с накладными.
	 * @param {object} data ответ от сервера
	 */
	setInvoices(data) {
		this.Invoices = data.Success ? _.map(data.Invoices, (item) => {
			var type = _.find(InvoiceTypes, { Id : item.InvoiceType }),
				paymentType = _.find(InvoicePaymentTypes, { Id : item.PaymentType }),
				color = item.Status === 1 ? "" : item.Status === 3 ? "payment"
					: isPaymentAlert(item.DeadlinePayment, this.Today) ? "alert" : "notPayment";

			return {
				...item,
				InvoiceNumber : (type ? type.ShortName : "") + " " + item.InvoiceNumber,
				PaymentIcon : paymentType ? paymentType.Icon : "",
				StatusName : _.find(InvoiceStatuses, { Id : item.Status }) ? _.find(InvoiceStatuses, { Id : item.Status }).Name : "",
				StatusColor : color,
				CanRemove : item.Status === 1 && this.CanAccept,
				CanPay : item.Status === 2 && this.CanPayment,
				CanEdit : item.Status === 1 && this.CanView,
				CanView : item.Status !== 1 && this.CanView
			};
		}) : [];

		this.HasMore = data.HasMore;
		this.IsSendRequest = true;
	}

	get columns () {
		var cols = {
			Date: { filterable : false, Header: "Дата", width : 85, Cell: row => formatDate(row.original.Date) },
			InvoiceNumber : { },
			Provider : { Cell: row => <span title={row.original.Provider}>{row.original.Provider}</span> },
			User: {}, Stock: {},
			Amount : {
				width: 100,
				Cell: row => <span className='amount'>{getRubs(row.original.Amount)} {row.original.PaymentIcon}</span>
			},
			DeadlinePayment: { width: 85, Cell: row => row.original.Status !== 2 ? "" : formatDate(row.original.DeadlinePayment) },
			Status: { filterable : false, width: 85, Cell: row => row.original.StatusName }
		};

		if (this.CanView || this.CanPayment) cols = {
			...cols, Edit : {}, View : {}, Remove : {}, Pay: {}
		};

		return cols;
	}

	/**
	 * Создание модели накладной.
	 * @param {object} invoice ответ от сервера
	 */
	setInvoice(invoice) {
		this.Invoice = !_.isUndefined(invoice) ? new Invoice(invoice, {
			CanManage : this.CanManage,
			CanAccept : this.CanAccept,
			CanPay : this.CanPayment
		}) : undefined;
	}
}
decorate(InvoiceState, {
	Providers: observable,
	ShopListList: observable,
	Filter: observable,
	Invoices: observable,
	IsSendRequest: observable,
	HasMore: observable,
	Invoice: observable,
	CanManage: computed,
	CanAccept: computed,
	CanPayment: computed,
	CanView: computed,
	Stocks: computed,
	ShopLists: computed,
	columns: computed
});

/** Класс фильтра для накладных */
export class Filter {
	constructor() {
		this.StockId = -1;
		this.ProviderId = -1;
		this.Date = "";
		this.Status = "";
		this.Limit = 100;
		this.Offset = 0;
	}

	/**
	 * Сбор данных с формы
	 * @param {string} id название параметра
	 * @param {string} value значение параметра
	 */
	collect(id, value) { this[id] = value; }
	/**
	 * Сбор данных для отправки на сервер
	 * @returns {{StockId : number, ProductId: number, Date: string, Status: number, Limit: number, Offset: number}}
	 */
	getData () { return clearEmptyParams(_.clone(this)); }
}
decorate (Filter, {
	StockId: observable,
	ProviderId: observable,
	Date: observable,
	Status: observable,
	Offset: observable
})

/** Класс конкретной накладной */
export class Invoice {
	constructor(item = {}, rights = {}) {
		this.Id = item && item.Id ? item.Id : -1;
		this.InvoiceType = item && item.InvoiceType ? item.InvoiceType : -1;
		this.InvoiceNumber = item && item.InvoiceNumber ? item.InvoiceNumber : "";
		this.Date = item && item.Date ? item.Date : "";
		this.StockId = item && item.StockId ? item.StockId : -1;
		this.Stock = item && item.Stock ? item.Stock : "";
		this.ProviderId = item && item.ProviderId ? item.ProviderId : -1;
		this.Provider = item && item.Provider ? item.Provider : "";
		this.Description = item && item.Description ? item.Description : "";
		this.Status = item && item.Status ? item.Status : 1;

		this.ShopListId = item && item.ShopListId ? item.ShopListId : -1;

		this.User = item && item.User ? item.User : "";
		this.AcceptUser = item && item.AcceptUser ? item.AcceptUser : "";
		this.AcceptDate = item && item.AcceptDate ? item.AcceptDate : "";
		this.DeadlinePayment = item && item.DeadlinePayment ? item.DeadlinePayment : "";
		this.PaymentDate = item && item.PaymentDate ? item.PaymentDate : "";
		this.PaymentType = item && item.PaymentType ? item.PaymentType : -1;
		this.PaymentUser = item && item.PaymentUser ? item.PaymentUser : "";

		this.ProviderProducts = [];
		this.Products = new InvoiceProducts(item.Products, this.Status, this.Id === -1, item.Amount, item.VATAmount);
		this.CanManageRight = Boolean(rights.CanManage);
		this.CanAcceptRight = Boolean(rights.CanAccept);
		this.CanPayRight = Boolean(rights.CanPay);

		this.ErrorCode = "";
		this.ButtonLoading = false;
		this.SuccessSave = false;
		this.ConfirmOpen = false;
		this.ConfirmLoading = false;
	}

	/** Флаги прав редактирования */
	get CanManage () { return (this.CanManageRight || this.CanAcceptRight) && this.Status === 1; }
	get CanAccept () { return this.CanAcceptRight && this.Status === 1 && this.Id !== -1; }
	get CanPay () { return this.CanPayRight && this.Status === 2; }

	/** Флаг статуса накладной */
	get isAccepted () { return this.Status > 1; }
	get isPaid () { return this.Status > 2; }

	/** Иконка выбранного способа оплаты */
	get PaymentIcon () {
		var type = _.find(InvoicePaymentTypes, { Id : this.PaymentType });
		return type ? type.Icon : "";
	}

	/** Название выбранного способа оплаты */
	get PaymentTitle () {
		var type = _.find(InvoicePaymentTypes, { Id : this.PaymentType });
		return type ? type.Name : "";
	}

	/** Количество дней до оплаты */
	get paymentDaysLeft () {
		if (this.isPaid) return "";
		if (!this.isAccepted && !this.isPaid) return "";

		return getPaymentDaysLeft(this.DeadlinePayment);
	}

	/** Заголовок страницы */
	get pageTitle () {
		return this.Id === -1 ? buttonTitles.NewInvoice : buttonTitles.ViewInvoice + " " +
					this.Title + " от " + formatDate(this.Date);
	}

	/** Название накладной из сокращённого типа и номера */
	get Title () {
		var type = _.find(InvoiceTypes, { Id : this.InvoiceType });
		return ((type ? type.ShortName : "") + " " + this.InvoiceNumber).trim();
	}

	/**
	 * Разбор данных от поставщика
	 * @param {object} provider ответ от сервера с данными поставщика
	 */
	setProviderData (provider) {
		this.ProviderProducts = _.map(provider.Products, (product) => {
			return { ...product, Id : product.ProductId, Name : product.Product };
		});
		this.Products.setProviderData(this.ProviderProducts);
	}

	/** 
	 * Разбор данных из листа закупки
	 * @param {object} shopList ответ от сервера со списком закупки
	 */
	setShopListData (shopList) {
		this.StockId = shopList && shopList.StockId ? shopList.StockId : this.StockId;
		this.ProviderId = shopList && shopList.ProviderId ? shopList.ProviderId : this.ProviderId;
		this.Products.setList(shopList && shopList.Products ? shopList.Products : []);
	}
	
	/**
	 * Сбор данных с формы
	 * @param {string} id название параметра
	 * @param {string} value значение параметра
	 * @param {number} index индекс продукта
	 */
	collect (id, value) { this[id] = value; }

	/**
	 * Проверка обязательных номера и типа накладной, даты не позднее текущей,
	 * склада, поставщика, способа оплаты, наличия товаров поставщика,
	 * положительных количества, цены и суммы строк и итоговой суммы,
	 * а также неотрицательных сумм НДС строки и накладной.
	 * @returns {boolean} результат проверки
	 */
	validate () {
		this.ErrorCode = "";
		var isValid = true;

		if (this.InvoiceNumber === "" || this.InvoiceType === "" || this.InvoiceType === -1) {
			this.ErrorCode ="INVOICE_NUMBER_REQUIRED";
			return false;
		}

		if (this.Date === "") {
			this.ErrorCode ="INVOICE_DATE_REQUIRED";
			return false;
		}

		if (this.StockId === "" || this.StockId === -1) {
			this.ErrorCode ="STOCK_REQUIRED";
			return false;
		}

		if (this.ProviderId === "" || this.ProviderId === -1) {
			this.ErrorCode ="PROVIDER_REQUIRED";
			return false;
		}

		if (this.PaymentType === "" || this.PaymentType === -1) {
			this.ErrorCode ="PAYMENTTYPE_REQUIRED";
			return false;
		}

		var products = this.Products.FilledList;

		if (_.isEmpty(products)) {
			this.ErrorCode ="INVOICE_PRODUCT_REQUIRED";
			return false;
		}

		_.each(products, (v) => {
			if (Number(v.Count) <= 0 || Number(v.CountGross) <= 0) {
				this.ErrorCode ="COUNT_REQUIRED";
				isValid = false;
				return false;
			}

			if (Number(v.Price) <= 0 || Number(v.Amount) <= 0 || v.VAT === "" || v.VAT === -1 || Number(v.VATAmount) < 0) {
				this.ErrorCode ="P_AMOUNT_REQUIRED";
				isValid = false;
				return false;
			}
		});

		if (Number(this.Products.Amount) <= 0 || Number(this.Products.VATAmount) < 0) {
			this.ErrorCode ="AMOUNT_REQUIRED";
			return false;
		}

		if (_.some(products, (product) => product.Id === -1 && !_.find(this.ProviderProducts, { Id : product.ProductId }))) {
			this.ErrorCode = "INVOICE_PRODUCT_REQUIRED";
			return false;
		}

		return isValid;
	}

	/** Сбор данных для отправки на сервер */
	getData () {
		var data = {
				Id : this.Id,
				InvoiceType : this.InvoiceType,
				InvoiceNumber : this.InvoiceNumber,
				Date : this.Date,
				StockId : this.StockId,
				ProviderId : this.ProviderId,
				Description : this.Description,
				Amount : (this.Products.Amount*100).toFixed(0),
				VATAmount : (this.Products.VATAmount*100).toFixed(0),
				PaymentType: this.PaymentType,
			};

		_.each(this.Products.getData(), (product, index) => {
			var name = "Products." + index + ".";
			_.each(product, (value, field) => { data[name + field] = value; });
		});

		if (this.ShopListId !== -1) data.ShopListId = this.ShopListId;

		return data;
	}
}
decorate(Invoice, {
	InvoiceType : observable,
	InvoiceNumber: observable,
	Date: observable,
	StockId: observable,
	ProviderId: observable,
	Description: observable,
	PaymentType: observable,

	ShopListId: observable,

	Products: observable,
	ProviderProducts: observable,

	ErrorCode: observable,
	ButtonLoading: observable,
	SuccessSave: observable,
	ConfirmOpen: observable,
	ConfirmLoading: observable,
	CanManage: computed,
	CanAccept: computed,
	CanPay: computed,
	isAccepted: computed,
	isPaid: computed,
	PaymentIcon: computed,
	PaymentTitle: computed,
	paymentDaysLeft: computed,
	pageTitle: computed,
	Title: computed
})

/** Модель списка продуктов накладной */
export class InvoiceProducts {
	constructor(items, status = 1, isNew = false, amount = "", vatAmount = "") {
		this.List = _.map(items || [], (item) => new InvoiceProduct(item, status));
		if (isNew && _.isEmpty(this.List)) this.List.push(new InvoiceProduct());
		this.Amount = amount || amount === 0 ? getRubs(amount) : "";
		this.VATAmount = vatAmount || vatAmount === 0 ? getRubs(vatAmount) : "";
	}

	/** Заполненные строки продуктов */
	get FilledList () { return _.filter(this.List, (item) => item.ProductId !== -1 && item.ProductId !== ""); }

	/** Добавление строки продукта */
	addItem () { this.List.push(new InvoiceProduct()); }

	/** Удаление строки продукта с сохранением хотя бы одной строки */
	removeItem (index) {
		if (this.List.length <= 1) return false;
		this.List = _.filter(this.List, (item, itemIndex) => itemIndex !== index);
		this.setSummary();
	}

	/** Замена строк данными листа закупки */
	setList (items) {
		this.List = _.map(items || [], (item) => new InvoiceProduct(item));
		this.setSummary();
	}

	/** Дополнение строк данными справочника поставщика */
	setProviderData (products) {
		_.each(this.List, (item) => item.setProviderData(_.find(products, { Id : item.ProductId })));
		this.setSummary();
	}

	/**
	 * Сбор значения строки или итогов.
	 * @param {string} id название поля
	 * @param {*} value значение
	 * @param {number} index индекс строки
	 * @param {object} product выбранный продукт поставщика
	 */
	collect (id, value, index, product) {
		if (_.isUndefined(index)) this[id] = value;
		else {
			this.List[index].collect(id, value, product);
			this.setSummary();
		}
	}

	/** Расчёт итоговых сумм */
	setSummary () {
		this.Amount = _.sumBy(this.FilledList, (item) => _.isNaN(parseFloat(item.Amount)) ? 0 : parseFloat(item.Amount)).toFixed(2);
		this.VATAmount = _.sumBy(this.FilledList, (item) => _.isNaN(parseFloat(item.VATAmount)) ? 0 : parseFloat(item.VATAmount)).toFixed(2);
	}

	/** Явные данные заполненных строк для API */
	getData () { return _.compact(_.map(this.List, (item) => item.getData())); }
}
decorate(InvoiceProducts, {
	List: observable,
	Amount: observable,
	VATAmount: observable,
	FilledList: computed
});

/** Класс продукта накладной */
export class InvoiceProduct {
	constructor (item, status = 1) {
		item = item || {};
		this.Id = item && item.Id ? item.Id : -1;
		this.ProductId = item && item.ProductId ? item.ProductId : -1;
		this.ProductName = item && item.ProductName ? item.ProductName : "";
		this.Count = item && item.Count ? item.Count : "";
		this.UnitNet = item && item.UnitNet ? item.UnitNet : "";
		this.Price = item && item.Price ? getRubs(item.Price, status !== 1, status !== 1) : "";
		this.CountGross = item && item.CountGross ? item.CountGross : "";
		this.UnitGross = item && item.UnitGross ? item.UnitGross : "";
		this.VAT = item && item.VAT ? item.VAT : -1;
		this.VATTitle = item && item.VAT && _.find(VATTitles, { Id : item.VAT }) ? _.find(VATTitles, { Id : item.VAT }).Name : "";
		this.VATAmount = item && item.VATAmount ? getRubs(item.VATAmount, status !== 1, status !== 1) : "";
		this.Amount = item && item.Amount ? getRubs(item.Amount, status !== 1, status !== 1) : "";
	}

	/** Дополнение незаполненных полей данными поставщика */
	setProviderData (product) {
		if (!product) return;
		this.UnitNet = this.UnitNet === "" ? product.UnitNet : this.UnitNet;
		this.UnitGross = this.UnitGross === "" ? product.UnitGross : this.UnitGross;
		this.Price = this.Price === "" ? getRubs(product.Price) : this.Price;
		this.VAT = this.VAT === -1 ? product.VAT : this.VAT;
		if (this.Amount === "" || this.Amount === 0) this.setAmounts();
	}

	/**
	 * Сбор значения строки и пересчёт зависимых полей.
	 * @param {string} id название поля
	 * @param {*} value значение
	 * @param {object} product выбранный продукт поставщика
	 */
	collect (id, value, product) {
		this[id] = value;
		if (id === "ProductId") {
			this.UnitNet = product ? product.UnitNet : "";
			this.UnitGross = product ? product.UnitGross : "";
			this.Price = product ? getRubs(product.Price) : "";
			this.VAT = product ? product.VAT : -1;
			this.setAmounts();
		} else if (id === "Count" || id === "Price") this.setAmounts();
		else if (id === "Amount") {
			if (Number(this.Count) > 0) this.Price = (Number(this.Amount) / Number(this.Count)).toFixed(2);
			this.setVATAmount();
		} else if (id === "VAT") this.setVATAmount();
	}

	/** Расчёт суммы строки и НДС */
	setAmounts () {
		this.Amount = (Number(this.Price) * Number(this.Count)).toFixed(2);
		this.setVATAmount();
	}

	/** Расчёт суммы НДС */
	setVATAmount () {
		var vat = _.find(VATTitles, { Id : this.VAT });
		this.VATAmount = vat ? (Number(this.Amount) * vat.Multi).toFixed(2) : "0.00";
	}

	/**
	 * Получение данных строки для API.
	 * @returns {{ProductId:number, Count:number, CountGross:number, Price:string, Amount:string, VAT:number, VATAmount:string}|null}
	 */
	getData () {
		if (this.ProductId === -1 || this.ProductId === "") return null;
		return {
			ProductId : this.ProductId,
			Count : this.Count,
			CountGross : this.CountGross,
			Price : (this.Price*100).toFixed(0),
			Amount : (this.Amount*100).toFixed(0),
			VAT : this.VAT,
			VATAmount : (this.VATAmount*100).toFixed(0)
		};
	}
}
decorate(InvoiceProduct, {
	ProductId: observable,
	Count: observable,
	UnitNet: observable,
	Price: observable,
	CountGross: observable,
	UnitGross: observable,
	VAT: observable,
	VATAmount: observable,
	Amount: observable,
})
